import { bestThumbUrl, wrapperToDirectUrl } from '@/utils/imageUrl'
import { syncedGalleryImages, customGalleryImages } from '@/utils/galleryImages'

// Advisory image preloads for the visitor's first paint. Computed on home publish
// (admin), stored as `preloadImages` on users/{uid}, injected at boot (visitor).
// Stale entries are harmless: they only warm the browser cache, rendering always
// comes from the real works data.

export const PRELOAD_IMAGES_MAX = 5

// A columns block's slots that are image slots with an actual uploaded image.
// Each slot is a mini-block: { type: 'image', data: { imageUrl, ... } }.
function filledImageSlots(block) {
  const slots = block.data?.slots || []
  return slots.filter(slot => slot?.type === 'image' && slot.data?.imageUrl)
}

// Scan the home page's blocks for the first image-bearing block and return the
// thumb URLs its first paint will request (same URLs the grid renders).
export function computePreloadImages(blocks, allWorks, max = PRELOAD_IMAGES_MAX) {
  const block = (blocks || []).find(candidate => {
    if (!candidate) return false
    if (candidate.type === 'gallery' || candidate.type === 'image') return true
    return candidate.type === 'columns' && filledImageSlots(candidate).length > 0
  })
  if (!block) return []

  if (block.type === 'image') {
    const url = block.data?.imageUrl || ''
    const direct = wrapperToDirectUrl(url) || url
    return direct ? [direct] : []
  }

  if (block.type === 'columns') {
    return filledImageSlots(block)
      .map(slot => wrapperToDirectUrl(slot.data.imageUrl) || slot.data.imageUrl)
      .slice(0, max)
  }

  // Gallery block: derive the display list with the exact same shared logic
  // GalleryBlock renders with, then take the first thumbs in display order.
  // GalleryItem renders bestThumbUrl(image) over these same objects, so the
  // preloaded URLs match the rendered <img> src byte-for-byte.
  const displayImages = block.data?.syncAllWorks === true
    ? syncedGalleryImages(allWorks, block.data?.syncTagIds)
    : customGalleryImages(block.data?.images || [], allWorks, {
        includeArchived: block.data?.includeArchived === true
      })

  return displayImages
    .slice(0, max)
    .map(bestThumbUrl)
    .filter(Boolean)
}

// Inject <link rel="preload" as="image"> so the browser fetches the first thumbs in
// parallel with the works query instead of after render. All at high priority: in
// masonry the LCP is the LARGEST first-row thumb, not the first, and a low-priority
// preload wins the request race against the grid's fetchpriority=high <img>.
// No `crossorigin`: <img> fetches are no-cors and must match to reuse the preload.
export function injectImagePreloads(urls) {
  if (!Array.isArray(urls)) return
  urls.slice(0, PRELOAD_IMAGES_MAX).forEach(url => {
    if (!url) return
    const alreadyInjected = document.head.querySelector(`link[rel="preload"][href="${CSS.escape(url)}"]`)
    if (alreadyInjected) return
    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'image'
    link.href = url
    link.fetchPriority = 'high'
    document.head.appendChild(link)
  })
}
